<template>
  <div class="shipments-wrapper">

    <!-- Toast Notifications -->
    <div class="notifications-wrapper">
      <div v-for="(n, index) in notifications" :key="index" :class="['notification', n.type]">
        {{ n.message }}
      </div>
    </div>

    <!-- Header -->
    <div class="header animated fadeInDown">
      <h2><i class="fas fa-truck-loading"></i> Shipments Dashboard</h2>
      <div class="header-actions">
        <button @click="addShipment" class="btn-gradient"><i class="fas fa-plus"></i> Add Shipment</button>
        <button @click="refreshShipments" class="btn-refresh"><i class="fas fa-sync-alt"></i> Refresh</button>
        <button @click="exportPDF" class="btn-pdf" :disabled="filteredShipments.length === 0"><i class="fas fa-file-pdf"></i> Export PDF</button>
        <button @click="exportExcel" class="btn-excel" :disabled="filteredShipments.length === 0"><i class="fas fa-file-excel"></i> Export Excel</button>
        <button @click="printLayout" class="btn-print" :disabled="filteredShipments.length === 0"><i class="fas fa-print"></i> Print</button>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="cards-grid animated fadeInUp">
      <div class="stat-card total" :class="{ active: filterStatus === '' }" @click="setStatus('')">
        <h3>{{ shipmentCount }}</h3>
        <p>Total Shipments</p>
      </div>
      <div class="stat-card active" :class="{ active: filterStatus==='active' }" @click="setStatus('active')">
        <h3>{{ activeShipments.length }}</h3>
        <p>Active</p>
      </div>
      <div class="stat-card completed" :class="{ active: filterStatus==='completed' }" @click="setStatus('completed')">
        <h3>{{ completedShipments.length }}</h3>
        <p>Completed</p>
      </div>
      <div class="stat-card paid" :class="{ active: filterStatus==='paid' }" @click="setStatus('paid')">
        <h3>{{ paidShipments.length }}</h3>
        <p>Paid</p>
      </div>
      <div class="stat-card unpaid" :class="{ active: filterStatus==='unpaid' }" @click="setStatus('unpaid')">
        <h3>{{ unpaidShipments.length }}</h3>
        <p>Unpaid</p>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters animated fadeInUp">
      <input v-model="search" type="text" placeholder="Search by ID, destination or supplier..." />
      <input v-model="destinationFilter" type="text" placeholder="Filter by destination" />
      <input v-model="supplierFilter" type="text" placeholder="Filter by supplier" />
      <input v-model="dateFrom" type="date" placeholder="From date" />
      <input v-model="dateTo" type="date" placeholder="To date" />

      <div class="pagination-controls-inline">
        <label>Per page:
          <select v-model.number="perPage">
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </label>
        <label>Sort:
          <select v-model="sortKey">
            <option value="deliveredDate">Delivery Date</option>
            <option value="destination">Destination</option>
            <option value="supplier">Supplier</option>
            <option value="paymentStatus">Payment Status</option>
          </select>
        </label>
        <button @click="toggleSortDir" class="btn-sort">Sort: {{ sortDir === 'desc' ? 'DESC' : 'ASC' }}</button>
      </div>
    </div>

    <!-- Table -->
    <div class="table-wrapper animated fadeInUp">
      <div v-if="loading" class="spinner">
        <i class="fas fa-spinner fa-spin"></i> Loading Shipments...
      </div>

      <table v-if="!loading && paginatedShipments.length" class="table">
        <thead>
          <tr>
            <th @click="setSort('rowNumber')"># <small class="sort-indicator">{{ headerSortIndicator('rowNumber') }}</small></th>
            <th @click="setSort('destination')">Destination <small class="sort-indicator">{{ headerSortIndicator('destination') }}</small></th>
            <th @click="setSort('supplier')">Supplier <small class="sort-indicator">{{ headerSortIndicator('supplier') }}</small></th>
            <th @click="setSort('approxCubes')">Approx Cubes <small class="sort-indicator">{{ headerSortIndicator('approxCubes') }}</small></th>
            <th @click="setSort('actualCubes')">Actual Cubes <small class="sort-indicator">{{ headerSortIndicator('actualCubes') }}</small></th>
            <th @click="setSort('deliveredDate')">Delivery Date <small class="sort-indicator">{{ headerSortIndicator('deliveredDate') }}</small></th>
            <th @click="setSort('deliveryStatus')">Delivery Status <small class="sort-indicator">{{ headerSortIndicator('deliveryStatus') }}</small></th>
            <th @click="setSort('paymentStatus')">Payment Status <small class="sort-indicator">{{ headerSortIndicator('paymentStatus') }}</small></th>
            <th @click="setSort('advancePayment')">Advance <small class="sort-indicator">{{ headerSortIndicator('advancePayment') }}</small></th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(shipment, idx) in paginatedShipments"
            :key="shipment.id"
            class="row-link"
            @click="goToDetails(shipment.id)"
          >
            <td>{{ startIndex + idx + 1 }}</td>
            <td>{{ shipment.destination }}</td>
            <td>{{ shipment.supplier }}</td>
            <td>{{ shipment.approxCubes ?? '-' }}</td>
            <td>{{ shipment.actualCubes ?? '-' }}</td>
            <td>{{ shipment.deliveredDate || '-' }}</td>
            <td><span class="badge" :class="deliveryBadgeClass(shipment.deliveryStatus)">{{ shipment.deliveryStatus || '-' }}</span></td>
            <td><span class="badge" :class="paymentBadgeClass(shipment.paymentStatus)">{{ shipment.paymentStatus || '-' }}</span></td>
            <td>{{ shipment.advancePayment | currency }}</td>
            <td>
              <button @click.stop="editShipment(shipment)" class="btn-small">Edit</button>
              <button @click.stop="goToDetails(shipment.id)" class="btn-small">View</button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- No-data (only show when not loading and truly empty) -->
      <p v-if="!loading && filteredShipments.length === 0" class="no-data">No shipments found.</p>

      <!-- Pagination Controls -->
      <div v-if="totalPages > 1" class="pagination">
        <button :disabled="page === 1" @click="goPage(page - 1)">Prev</button>

        <button
          v-for="p in pageRange"
          :key="p"
          @click="goPage(p)"
          :class="{ active: p === page }"
        >
          {{ p }}
        </button>

        <button :disabled="page === totalPages" @click="goPage(page + 1)">Next</button>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-console */
import { mapGetters } from "vuex";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default {
  name: "ShipmentsDashboard",
  data() {
    return {
      // logo path you provided
      logo: "/img/pacmislogo.png",

      search: "",
      filterStatus: "",
      destinationFilter: "",
      supplierFilter: "",
      dateFrom: "",
      dateTo: "",
      notifications: [],

      page: 1,
      perPage: 20,
      loading: false,

      // sorting
      sortKey: "deliveredDate",
      sortDir: "desc", // 'asc' or 'desc'
    };
  },

  computed: {
    ...mapGetters("shipments", [
      "allShipments",
      "activeShipments",
      "completedShipments",
      "shipmentCount",
    ]),

    paidShipments() {
      return this.allShipments.filter((s) => (s.paymentStatus || "").toLowerCase() === "paid");
    },

    unpaidShipments() {
      return this.allShipments.filter((s) => (s.paymentStatus || "").toLowerCase() !== "paid");
    },

    /** MAIN FILTER PIPELINE (returns filtered + sorted array) */
    filteredShipments() {
      let filtered = Array.isArray(this.allShipments) ? this.allShipments.slice() : [];

      // apply status filter (matches either deliveryStatus or paymentStatus)
      if (this.filterStatus) {
        filtered = filtered.filter((s) => {
          return (s.deliveryStatus === this.filterStatus) || (s.paymentStatus === this.filterStatus);
        });
      }

      // search (id, destination, supplier)
      if (this.search) {
        const q = this.search.toLowerCase();
        filtered = filtered.filter((s) => {
          const idStr = s.id ? String(s.id) : "";
          return `${idStr} ${s.destination || ""} ${s.supplier || ""}`.toLowerCase().includes(q);
        });
      }

      // destination
      if (this.destinationFilter) {
        const q = this.destinationFilter.toLowerCase();
        filtered = filtered.filter((s) => (s.destination || "").toLowerCase().includes(q));
      }

      // supplier
      if (this.supplierFilter) {
        const q = this.supplierFilter.toLowerCase();
        filtered = filtered.filter((s) => (s.supplier || "").toLowerCase().includes(q));
      }

      // date range
      if (this.dateFrom || this.dateTo) {
        const f = this.dateFrom ? new Date(this.dateFrom) : null;
        const t = this.dateTo ? new Date(this.dateTo) : null;
        filtered = filtered.filter((s) => {
          if (!s.deliveredDate) return false;
          const d = new Date(s.deliveredDate);
          if (f && t) return d >= f && d <= t;
          if (f) return d >= f;
          if (t) return d <= t;
          return true;
        });
      }

      // SORT
      const key = this.sortKey;
      filtered.sort((a, b) => {
        const getVal = (item) => {
          const v = item[key];
          if (key === "deliveredDate") {
            return v ? new Date(v).getTime() : 0;
          }
          if (v === null || v === undefined) return "";
          return typeof v === "string" ? v.toLowerCase() : v;
        };

        const va = getVal(a);
        const vb = getVal(b);

        if (va < vb) return this.sortDir === "asc" ? -1 : 1;
        if (va > vb) return this.sortDir === "asc" ? 1 : -1;
        return 0;
      });

      return filtered;
    },

    /** Pagination */
    paginatedShipments() {
      if (!this.filteredShipments.length) return [];
      const start = (this.page - 1) * this.perPage;
      return this.filteredShipments.slice(start, start + this.perPage);
    },

    totalPages() {
      if (!this.filteredShipments.length) return 0;
      return Math.ceil(this.filteredShipments.length / this.perPage);
    },

    startIndex() {
      return (this.page - 1) * this.perPage;
    },

    // range of pages to show (simple)
    pageRange() {
      const total = this.totalPages;
      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      // show current +/- 3
      const arr = [];
      let start = Math.max(1, this.page - 3);
      let end = Math.min(total, this.page + 3);
      if (end - start < 6) {
        // expand if near edges
        start = Math.max(1, end - 6);
        end = Math.min(total, start + 6);
      }
      for (let i = start; i <= end; i++) arr.push(i);
      return arr;
    },
  },

  methods: {
    // status badge classes
    deliveryBadgeClass(status) {
      if (!status) return "badge neutral";
      const s = String(status).toLowerCase();
      if (s === "completed") return "badge completed";
      if (s === "active") return "badge active";
      if (s === "overdue") return "badge overdue";
      return "badge neutral";
    },
    paymentBadgeClass(status) {
      if (!status) return "badge neutral";
      const s = String(status).toLowerCase();
      if (s === "paid") return "badge paid";
      if (s === "unpaid") return "badge unpaid";
      if (s === "partial" || s === "partial payment") return "badge partial";
      if (s === "overpaid") return "badge overpaid";
      return "badge neutral";
    },

    setStatus(v) {
      this.filterStatus = v;
    },

    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortDir = key === "deliveredDate" ? "desc" : "asc";
      }
    },

    headerSortIndicator(key) {
      return this.sortKey === key ? (this.sortDir === "asc" ? "▲" : "▼") : "";
    },

    toggleSortDir() {
      this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
    },

    showToast(message, type = "success", duration = 3500) {
      this.notifications.push({ message, type });
      setTimeout(() => {
        this.notifications.shift();
      }, duration);
    },

    async refreshShipments() {
      try {
        this.loading = true;
        this.showToast("Loading shipments...", "loading");
        await this.$store.dispatch("shipments/fetchShipments");
        this.showToast("Shipments loaded", "success");
      } catch (err) {
        this.showToast("Error: " + (err.message || err), "error");
      } finally {
        this.loading = false;
      }
    },

    addShipment() {
      this.$router.push({ name: "AddShipment" });
    },

    editShipment(s) {
      this.$router.push({ name: "EditShipment", params: { id: s.id } });
    },

    goToDetails(id) {
      this.$router.push({ name: "ShipmentDetails", params: { id } });
    },

    goPage(p) {
      if (!p || p < 1) return;
      if (p > this.totalPages) return;
      this.page = p;
      // scroll to top of table (optional)
      const wrapper = this.$el.querySelector(".table-wrapper");
      if (wrapper) wrapper.scrollIntoView({ behavior: "smooth", block: "start" });
    },

    // load an image as dataURL (defensive)
    loadImageAsDataURL(url) {
      return new Promise((resolve, reject) => {
        if (!url) return resolve(null);
        // Try fetch -> blob -> dataURL
        fetch(url, { mode: "cors" })
          .then((resp) => {
            if (!resp.ok) throw new Error("Failed to fetch image");
            return resp.blob();
          })
          .then((blob) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = (e) => reject(e);
            reader.readAsDataURL(blob);
          })
          .catch((err) => {
            // fallback attempt with Image + canvas (may be blocked by CORS)
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
              try {
                const canvas = document.createElement("canvas");
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0);
                const data = canvas.toDataURL();
                resolve(data);
              } catch (e) {
                // final fallback: resolve null so PDF still generates
                console.warn("Image toDataURL fallback failed:", e);
                resolve(null);
              }
            };
            img.onerror = (e) => {
              console.warn("Image load failed:", e);
              resolve(null);
            };
            img.src = url;
          });
      });
    },

    /** Create PDF with header/logo, footer/disclaimer, page numbers and status color bands */
    async exportPDF() {
      try {
        if (!this.filteredShipments.length) {
          this.showToast("No data to export", "error");
          return;
        }

        this.showToast("Preparing PDF...", "loading");

        // Try to load logo (may fail due to CORS) - handle gracefully
        let logoData = null;
        try {
          logoData = await this.loadImageAsDataURL(this.logo);
        } catch (err) {
          console.warn("Could not load logo for PDF:", err);
          logoData = null;
        }

        const doc = new jsPDF({ orientation: "landscape" });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const title = "Shipments Report";
        const generated = "Generated: " + new Date().toLocaleString();

        // Build rows
        const rows = this.filteredShipments.map((s, idx) => [
          idx + 1,
          s.destination || "-",
          s.supplier || "-",
          s.approxCubes ?? "-",
          s.actualCubes ?? "-",
          s.deliveredDate || "-",
          s.deliveryStatus || "-",
          s.paymentStatus || "-",
          typeof s.advancePayment !== "undefined" && s.advancePayment !== null ? s.advancePayment : "-",
        ]);

        autoTable(doc, {
          startY: 36,
          head: [[
            "#",
            "Destination",
            "Supplier",
            "Approx Cubes",
            "Actual Cubes",
            "Delivery Date",
            "Delivery Status",
            "Payment Status",
            "Advance",
          ]],
          body: rows,
          styles: { fontSize: 9 },
          headStyles: { fillColor: [230, 230, 230], textColor: 20 },
          theme: "grid",
          margin: { top: 36, bottom: 20 },

          didDrawPage: (dataArg) => {
            // Header
            if (logoData) {
              try {
                const imgProps = doc.getImageProperties(logoData);
                const imgW = 36;
                const imgH = (imgProps.height * imgW) / imgProps.width;
                doc.addImage(logoData, "PNG", 14, 8, imgW, imgH);
              } catch (e) {
                // if addImage fails for any reason, continue without logo
                console.warn("doc.addImage failed:", e);
              }
            }
            doc.setFontSize(18);
            doc.text(title, pageWidth / 2, 18, { align: "center" });
            doc.setFontSize(10);
            doc.text(generated, pageWidth - 14, 18, { align: "right" });

            // Footer - disclaimer
            doc.setFontSize(9);
            const disclaimer = "Disclaimer: This report is system-generated and may contain unverified data.";
            doc.text(disclaimer, 14, pageHeight - 10);

            // Page number
            const pageNumber = `Page ${doc.internal.getNumberOfPages()}`;
            doc.text(pageNumber, pageWidth - 14, pageHeight - 10, { align: "right" });
          },

          // Defensive coloring of cells only for body cells
          didParseCell: (cell) => {
            try {
              if (!cell || cell.section !== "body") return;

              // ensure styles object exists
              if (!cell.styles) cell.styles = {};

              const colIndex = cell.column.index;
              const rowIndex = cell.row.index;
              const rowData = this.filteredShipments[rowIndex] || {};

              // deliveryStatus column (index 6)
              if (colIndex === 6) {
                const status = (rowData.deliveryStatus || "").toLowerCase();
                if (status === "completed") cell.styles.fillColor = [25, 135, 84]; // green
                else if (status === "active") cell.styles.fillColor = [13, 110, 253]; // blue
                else if (status === "overdue") cell.styles.fillColor = [220, 53, 69]; // red
                else cell.styles.fillColor = [245, 245, 245];
                // choose readable text color for dark backgrounds
                const dark = (cell.styles.fillColor || []).join(",") === "25,135,84" ||
                             (cell.styles.fillColor || []).join(",") === "13,110,253" ||
                             (cell.styles.fillColor || []).join(",") === "220,53,69";
                cell.styles.textColor = dark ? 255 : 35;
              }

              // paymentStatus column (index 7)
              if (colIndex === 7) {
                const p = (rowData.paymentStatus || "").toLowerCase();
                if (p === "paid") cell.styles.fillColor = [40, 167, 69]; // green
                else if (p === "unpaid") cell.styles.fillColor = [255, 193, 7]; // yellow
                else if (p === "overpaid") cell.styles.fillColor = [23, 162, 184]; // teal
                else if (p === "partial" || p === "partial payment") cell.styles.fillColor = [255, 160, 0]; // orange-ish
                else cell.styles.fillColor = [245, 245, 245];
                const darkFills = ["40,167,69", "220,53,69", "13,110,253"];
                const colorKey = (cell.styles.fillColor || []).join(",");
                cell.styles.textColor = darkFills.includes(colorKey) ? 255 : 35;
              }
            } catch (err) {
              // Do not break PDF generation for cell styling errors
              console.warn("didParseCell styling error:", err);
            }
          },
        });

        doc.save(`shipments_report_${Date.now()}.pdf`);
        this.showToast("PDF exported successfully", "success");
      } catch (err) {
        console.error("PDF error:", err);
        this.showToast("Error creating PDF: " + (err.message || err), "error");
      }
    },

    /** Export to Excel using SheetJS (xlsx) - dynamic import with fallback message */
    async exportExcel() {
      try {
        if (!this.filteredShipments.length) {
          this.showToast("No data to export", "error");
          return;
        }
        this.showToast("Preparing Excel...", "loading");

        // Try to import the full xlsx build
        let XLSX;
        try {
          XLSX = (await import(/* webpackChunkName: "xlsx" */ "xlsx/dist/xlsx.full.min.js")).default || (await import("xlsx")).default;
        } catch (err) {
          // Try plain import name (some setups)
          try {
            XLSX = (await import("xlsx")).default;
          } catch (e) {
            console.error("xlsx import failed:", e);
            this.showToast("xlsx library not found. Run `npm i xlsx` (or yarn add xlsx).", "error", 7000);
            return;
          }
        }

        // Build worksheet data (header + rows)
        const header = [
          "#",
          "Destination",
          "Supplier",
          "Approx Cubes",
          "Actual Cubes",
          "Delivery Date",
          "Delivery Status",
          "Payment Status",
          "Advance",
        ];

        const data = this.filteredShipments.map((s, idx) => [
          idx + 1,
          s.destination || "",
          s.supplier || "",
          s.approxCubes ?? "",
          s.actualCubes ?? "",
          s.deliveredDate || "",
          s.deliveryStatus || "",
          s.paymentStatus || "",
          s.advancePayment ?? "",
        ]);
        data.unshift(header);

        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Shipments");

        // write and trigger download
        const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([wbout], { type: "application/octet-stream" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `shipments_${Date.now()}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        this.showToast("Excel file ready", "success");
      } catch (err) {
        console.error("Excel export error:", err);
        this.showToast("Error creating Excel: " + (err.message || err), "error");
      }
    },

    /** Print-friendly layout (opens a new window and prints full filtered table) */
    printLayout() {
      const rowsHtml = this.filteredShipments.map((s, idx) => {
        return `<tr>
          <td style="padding:6px;border:1px solid #ccc;">${idx + 1}</td>
          <td style="padding:6px;border:1px solid #ccc;">${s.destination || ""}</td>
          <td style="padding:6px;border:1px solid #ccc;">${s.supplier || ""}</td>
          <td style="padding:6px;border:1px solid #ccc;">${s.approxCubes ?? ""}</td>
          <td style="padding:6px;border:1px solid #ccc;">${s.actualCubes ?? ""}</td>
          <td style="padding:6px;border:1px solid #ccc;">${s.deliveredDate || ""}</td>
          <td style="padding:6px;border:1px solid #ccc;">${s.deliveryStatus || ""}</td>
          <td style="padding:6px;border:1px solid #ccc;">${s.paymentStatus || ""}</td>
          <td style="padding:6px;border:1px solid #ccc;">${typeof s.advancePayment !== "undefined" ? s.advancePayment : ""}</td>
        </tr>`;
      }).join("");

      const html = `
        <html>
        <head>
          <title>Shipments Report</title>
          <style>
            body { font-family: Arial, Helvetica, sans-serif; margin:20px; color:#212529; }
            .report-header { display:flex; align-items:center; gap:16px; margin-bottom:14px; }
            .report-header img { height:48px; }
            table { width:100%; border-collapse: collapse; }
            th { background:#f1f3f5; padding:8px; border:1px solid #ddd; text-align:left; }
            td { padding:6px; border:1px solid #ddd; }
            .disclaimer { margin-top:12px; font-size:12px; color:#6c757d; }
          </style>
        </head>
        <body>
          <div class="report-header">
            <img src="${this.logo}" alt="logo" />
            <div>
              <h2 style="margin:0;">Shipments Report</h2>
              <div style="font-size:12px;color:#6c757d;">Generated: ${new Date().toLocaleString()}</div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Destination</th>
                <th>Supplier</th>
                <th>Approx Cubes</th>
                <th>Actual Cubes</th>
                <th>Delivery Date</th>
                <th>Delivery Status</th>
                <th>Payment Status</th>
                <th>Advance</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>

          <div class="disclaimer">Disclaimer: This report is system-generated and may contain unverified data.</div>
        </body>
        </html>
      `;

      const w = window.open("", "_blank", "toolbar=0,location=0,menubar=0");
      if (!w) {
        this.showToast("Popup blocked. Allow popups to use Print feature.", "error");
        return;
      }
      w.document.write(html);
      w.document.close();
      setTimeout(() => w.print(), 600);
    },
  },

  filters: {
    currency(value) {
      return value || value === 0 ? "Ksh " + Number(value).toLocaleString() : "Ksh 0";
    }
  },

  mounted() {
    this.sortKey = "deliveredDate";
    this.sortDir = "desc";
    this.refreshShipments();
  },

  watch: {
    // Reset to page 1 when filters or perPage change
    search() { this.page = 1; },
    destinationFilter() { this.page = 1; },
    supplierFilter() { this.page = 1; },
    dateFrom() { this.page = 1; },
    dateTo() { this.page = 1; },
    filterStatus() { this.page = 1; },
    perPage() { this.page = 1; },
  },
};
</script>

<style scoped>
/* general */
.shipments-wrapper { padding: 20px; background: #f4f6f9; }

/* header */
.header { display:flex; justify-content: space-between; align-items: center; margin-bottom:20px; }
.header-actions button { margin-left:8px; }

/* buttons */
.btn-gradient, .btn-pdf, .btn-refresh, .btn-excel, .btn-print, .btn-sort {
  padding: 10px 12px; border-radius: 8px; border:none; color:#fff; cursor:pointer; transition:0.15s;
}
.btn-gradient { background: linear-gradient(45deg,#6a11cb,#2575fc); } .btn-gradient:hover { transform: scale(1.02); }
.btn-pdf { background: #d63333; } .btn-pdf:hover { transform: scale(1.02); }
.btn-refresh { background: #20c997; } .btn-refresh:hover { transform: scale(1.02); }
.btn-excel { background: #107c10; } .btn-print { background:#6c757d; }
.btn-sort { background:#343a40; }

/* stats cards */
.cards-grid { display:grid; grid-template-columns: repeat(5,1fr); gap:15px; margin-bottom:20px; }
.stat-card { padding:20px; border-radius:14px; text-align:center; box-shadow:0 5px 15px rgba(0,0,0,0.07); cursor:pointer; transition:0.3s; }
.stat-card.total { background: linear-gradient(45deg,#6c757d,#adb5bd); }
.stat-card.active { background: linear-gradient(45deg,#0d6efd,#74c0fc); }
.stat-card.completed { background: linear-gradient(45deg,#198754,#63e6be); }
.stat-card.paid { background: linear-gradient(45deg,#28a745,#51cf66); }
.stat-card.unpaid { background: linear-gradient(45deg,#ffc107,#ff922b); }
.stat-card.active, .stat-card.completed, .stat-card.paid, .stat-card.unpaid, .stat-card.total.active { box-shadow:0 0 15px rgba(0,0,0,0.2); transform:translateY(-3px); }

/* filters */
.filters { display:flex; flex-wrap:wrap; gap:10px; align-items:center; margin-bottom:12px; }
.filters input { padding:10px; border-radius:8px; border:1px solid #ccc; min-width:180px; }
.pagination-controls-inline { margin-left: auto; display:flex; gap:10px; align-items:center; }

/* table */
.table-wrapper { overflow-x:auto; background:#fff; border-radius:12px; padding:8px; box-shadow:0 6px 18px rgba(0,0,0,0.03); }
.table { width:100%; border-collapse:collapse; margin-top:6px; }
.table thead th { background:#e9ecef; padding:10px; text-align:left; cursor:pointer; user-select:none; }
.table tbody td { padding:10px; border-bottom:1px solid #f1f1f1; vertical-align:middle; }

/* clickable rows */
.table tbody tr {
  cursor: pointer;
  transition: background 0.12s ease;
}
.table tbody tr:hover { background: #f5f7fa; }

/* badges */
.badge { padding:6px 10px; border-radius:6px; color:#fff; text-transform:capitalize; font-size:0.85rem; display:inline-block; }
.badge.completed { background:#198754; }
.badge.active { background:#0d6efd; }
.badge.overdue { background:#dc3545; }
.badge.paid { background:#28a745; }
.badge.unpaid { background:#ffc107; color:#000; }
.badge.partial { background:#ff8c00; color:#fff; }
.badge.overpaid { background:#17a2b8; }

/* small helpers */
.sort-indicator { font-size:10px; margin-left:6px; color:#666; }

/* pagination */
.pagination { margin-top: 15px; display: flex; gap: 6px; flex-wrap:wrap; align-items:center; }
.pagination button { padding: 6px 12px; border-radius: 6px; border: 1px solid #bbb; background: #fff; cursor: pointer; }
.pagination button.active { background: #0d6efd; color: white; border-color: #0d6efd; }

/* notifications */
.notifications-wrapper {
  position: fixed;
  top: 20px;
  right: 20px;
  display:flex;
  flex-direction:column;
  gap:10px;
  z-index: 9999;
}
.notification {
  padding: 12px 18px;
  border-radius: 8px;
  color:#fff;
  font-weight:500;
  min-width:200px;
  box-shadow: 0 3px 10px rgba(0,0,0,0.2);
}
.notification.success { background: #28a745; }
.notification.error { background: #dc3545; }
.notification.loading { background: #0d6efd; }

/* no-data */
.no-data { text-align:center; padding:20px; color:#6c757d; }

/* responsive */
@media (max-width: 900px) {
  .cards-grid { grid-template-columns: repeat(2,1fr); }
  .filters { gap:8px; }
}
</style>
